import React, { useEffect, useState } from 'react';
import { ADMIN } from '@/lib/adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, XCircle, Key, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from '@/context/AdminAuthContext';

function IntegrationDot({ ok }) {
  return ok ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Connected</span>
    : <span className="inline-flex items-center gap-1 text-rose-600"><XCircle className="h-4 w-4" /> Not configured</span>;
}

export default function AdminSettings() {
  const { user } = useAdminAuth();
  const [integ, setInteg] = useState(null);
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { ADMIN.integrations().then(setInteg).catch(() => setInteg(null)); }, []);

  const change = async () => {
    if (!cur || !next || !confirm) { toast.error('All fields required'); return; }
    if (next.length < 8) { toast.error('Password must be 8+ characters'); return; }
    if (next !== confirm) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await ADMIN.changePassword(cur, next);
      toast.success('Password updated');
      setCur(''); setNext(''); setConfirm('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings-view">
      <header><h1 className="text-2xl font-bold text-slate-900">Settings</h1><p className="text-sm text-slate-500 mt-1">Profile, security, and integrations.</p></header>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Profile card */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
          <h2 className="text-base font-bold text-slate-900 mb-3">Profile</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Name</span><span className="font-semibold text-slate-900">{user?.name}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="font-semibold text-slate-900">{user?.email}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Role</span><span className="font-semibold text-slate-900 capitalize">{user?.role}</span></div>
          </div>
        </div>

        {/* Password change */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
          <h2 className="text-base font-bold text-slate-900 mb-3 inline-flex items-center gap-2"><Key className="h-4 w-4" /> Change Password</h2>
          <div className="space-y-3">
            <div><Label className="text-xs">Current password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="settings-cur-pwd" /></div>
            <div><Label className="text-xs">New password</Label><Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="settings-new-pwd" /></div>
            <div><Label className="text-xs">Confirm new password</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="settings-confirm-pwd" /></div>
            <Button onClick={change} disabled={saving} className="w-full bg-[#0A2540] hover:bg-[#13294a]" data-testid="settings-pwd-save">{saving ? 'Saving…' : 'Update password'}</Button>
          </div>
        </div>
      </div>

      {/* Business info */}
      <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
        <h2 className="text-base font-bold text-slate-900 mb-3 inline-flex items-center gap-2"><Building2 className="h-4 w-4" /> Business Information</h2>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <Info label="Company" value="Elite Roofing and Construction LLC" />
          <Info label="Phone" value="(407) 785-0642" />
          <Info label="Address" value="780 Academy Drive, Suite 102, Kissimmee, FL 34744" />
          <Info label="License" value="CGC032777 (Florida Certified GC)" />
          <Info label="Service Area" value="Osceola County / Central Florida" />
          <Info label="Email" value="jeremy@elitercfl.com" />
        </div>
      </div>

      {/* Integrations */}
      <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
        <h2 className="text-base font-bold text-slate-900 mb-3">Integrations</h2>
        {!integ ? <p className="text-sm text-slate-500">Loading…</p> : (
          <div className="space-y-3 text-sm">
            <Row label="BabyLoveGrowth API" status={<>
              <IntegrationDot ok={integ.babylovegrowth.configured} />
              <button onClick={() => window.location.assign('/admin/integrations/babylovegrowth')} className="ml-3 text-xs font-semibold text-[#0A2540] hover:underline" data-testid="settings-blg-link">View publish log →</button>
            </>} note="Env: BABYLOVEGROWTH_API_KEY" />
            <Row label="SendGrid (Email)" status={<><IntegrationDot ok={integ.sendgrid.configured} />{integ.sendgrid.dry_run && <span className="ml-2 text-amber-700 text-xs">(dry-run on)</span>}</>} note="Env: SENDGRID_API_KEY" />
            <Row label="Dropbox" status={<IntegrationDot ok={integ.dropbox.app_key_set && integ.dropbox.app_secret_set && integ.dropbox.refresh_token_set} />} note="Env: DROPBOX_APP_KEY, DROPBOX_APP_SECRET, DROPBOX_REFRESH_TOKEN (P2)" />
            <Row label="reCAPTCHA v3" status={<IntegrationDot ok={integ.recaptcha.configured} />} note="Env: RECAPTCHA_SITE_KEY, RECAPTCHA_SECRET_KEY (optional)" />
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return <div className="rounded-lg bg-slate-50 p-3"><div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">{label}</div><div className="text-sm text-slate-800 mt-0.5">{value}</div></div>;
}

function Row({ label, status, note }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-slate-50">
      <div>
        <div className="font-semibold text-slate-900">{label}</div>
        {note && <div className="text-xs text-slate-500">{note}</div>}
      </div>
      <div className="text-sm font-semibold">{status}</div>
    </div>
  );
}
